import {
    useAppError,
    useGetErrorInfo,
} from '@experiences/error';
import type { LanguageCulture } from '@experiences/locales';
import {
    getUserLanguageFromLocalStorage,
    useUpdateLanguage,
} from '@experiences/locales';
import {
    HomePageEvent,
    portalTelemetry,
} from '@experiences/telemetry';
import {
    getDominantPersona,
    PERSONA_PRIORITY_ORDER,
    UiStorage,
} from '@experiences/util';
import {
    useCallback,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import { UserRole } from '../../common/constants/Constant';
import { getPersona } from '../../services/organization/Persona.default';
import { getUserOrganizationInfo } from '../../services/organization/UserOrganizationInfoService.default';
import { setUserProfile } from '../../store/action/UserProfileAction';
import useUserInfo from './UserInfo';

export default function useInit() {
    const [ initializing, setInitializing ] = useState(false);
    const { formatMessage: translate } = useIntl();
    const { getErrorObject } = useGetErrorInfo();

    const updateLanguage = useUpdateLanguage();

    const {
        ErrorComponent, renderError, setError, clearError,
    } = useAppError('login');

    const { token } = useUserInfo();

    const changeLanguage = useCallback(
        (id: LanguageCulture) => updateLanguage(id),
        [ updateLanguage ],
    );

    const init = useCallback(async () => {
        try {
            setInitializing(true);

            const [ userOrganizationInfo, personaFit ] = await Promise.all([
                getUserOrganizationInfo(token),
                getPersona(token),
            ]);

            userOrganizationInfo.persona = getDominantPersona(
                personaFit,
                userOrganizationInfo?.accountRoleType !== UserRole.DEFAULT_USER,
                userOrganizationInfo?.accountUserDto?.accountType,
                PERSONA_PRIORITY_ORDER);

            UiStorage.setItem('PORTAL_CURRENT_USERID', userOrganizationInfo.userGlobalId);
            portalTelemetry.trackTrace({
                message: `${HomePageEvent.InitializePersona} - get persona`,
                properties: {
                    code: '[IN-HP-04]',
                    PersonaFit: personaFit,
                    AccountType: userOrganizationInfo?.accountUserDto?.accountType,
                    Persona: userOrganizationInfo.persona,
                },
            });

            await setUserProfile(userOrganizationInfo);

            changeLanguage(
                getUserLanguageFromLocalStorage(userOrganizationInfo?.accountUserDto?.accountLanguage ?? 'en')
            );
        } catch (error) {
            const errorObject = await getErrorObject(error);
            setError(
                error,
                'Error occurred while getting user organization info',
                clearError,
                {
                    title: translate({ id: 'CLIENT_ERROR_ORGANIZATION_INFO' }),
                    message: errorObject,
                },
            );
        } finally {
            setInitializing(false);
        }
    }, [
        token,
        changeLanguage,
        getErrorObject,
        setError,
        clearError,
        translate,
    ]);

    return {
        init,
        initializing,
        ErrorComponent,
        renderError,
    };
}
